import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { BASE_URL } from "../seo/pages";
import { PublicHeader } from "../components/PublicHeader";
import { authFetch, getApiBase, getPathPrefix, resolveMediaUrl } from "../../utils/preview";
import "../styles/PublicCommon.css";
import "./WorksDetailPage.css";

// APIから取得するデータの型定義
interface ResultCategory {
  id: string;
  name: string;
  description: string;
}

interface ResultMedia {
  name?: string;
  size?: number;
  content_type?: string;
  media_type?: string;
  storage_path: string;
  display_order?: number;
  caption?: string;
}

interface Result {
  id: string;
  title: string;
  category_id: string;
  completion_date: string;
  main_image: ResultMedia | null;
  gallery_images: ResultMedia[];
  videos: ResultMedia[];
  client_name: string;
  location: string;
  site_area: string;
  building_area: string;
  total_floor_area: string;
  structure_type: string;
  description: string;
  display_priority: number;
  is_published: boolean;
}

interface ResultDetailResponse {
  result: Result;
  category: ResultCategory | null;
}

// 日付をフォーマットする関数
const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${date.getFullYear()}年${date.getMonth() + 1}月`;
};

export const WorksDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const pathPrefix = getPathPrefix();
  const [result, setResult] = useState<Result | null>(null);
  const [category, setCategory] = useState<ResultCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 存在しない ID（API が 404）のときは検索結果に載せない。一時的な取得失敗では付けない
  const [notFound, setNotFound] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  // APIからデータを取得
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await authFetch(`${getApiBase()}/results/${id}`);
        if (!response.ok) {
          if (response.status === 404) {
            setNotFound(true);
            throw new Error("施工実績が見つかりませんでした");
          }
          throw new Error("データの取得に失敗しました");
        }
        const data: ResultDetailResponse = await response.json();
        setResult(data.result);
        setCategory(data.category);
        setSelectedIndex(0);
        setError(null);
        setNotFound(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "エラーが発生しました");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // 全メディアリストを構築（main_image → gallery_images → videos の順）
  type MediaEntry = { url: string; type: "image" | "video"; caption?: string };
  const allMedia: MediaEntry[] = result
    ? [
        ...(result.main_image?.storage_path
          ? [
              {
                url: resolveMediaUrl(result.main_image.storage_path),
                type: "image" as const,
                caption: result.main_image.caption,
              },
            ]
          : []),
        ...(result.gallery_images || [])
          .filter((g) => g?.storage_path)
          .map((g) => ({
            url: resolveMediaUrl(g.storage_path),
            type: "image" as const,
            caption: g.caption,
          })),
        ...(result.videos || [])
          .filter((v) => v?.storage_path)
          .map((v) => ({
            url: resolveMediaUrl(v.storage_path),
            type: "video" as const,
            caption: v.caption,
          })),
      ]
    : [];
  const selectedMedia = allMedia[selectedIndex];

  // 構造化データを生成
  const structuredData = useMemo(() => {
    if (!result) return undefined;

    // 施工実績の構造化データ（CreativeWork / Project）
    const projectSchema = {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      "@id": `${BASE_URL}/works/${id}`,
      "name": result.title,
      "description": result.description || `${result.title}の施工実績詳細`,
      "dateCreated": result.completion_date,
      "datePublished": result.completion_date,
      "author": {
        "@type": "Organization",
        "name": "都建設株式会社",
        "url": BASE_URL,
      },
      "provider": {
        "@type": "Organization",
        "name": "都建設株式会社",
        "url": BASE_URL,
      },
      ...(result.main_image?.storage_path && {
        "image": (() => {
          const u = resolveMediaUrl(result.main_image.storage_path);
          return u.startsWith("http") ? u : `${BASE_URL}${u}`;
        })(),
      }),
      "locationCreated": {
        "@type": "Place",
        "name": result.location || "愛媛県西予市",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "愛媛県",
          "addressLocality": result.location || "西予市",
          "addressCountry": "JP",
        },
      },
      ...(category && {
        "genre": category.name,
      }),
    };

    // パンくずリストの構造化データ
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": BASE_URL,
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "施工実績",
          "item": `${BASE_URL}/works`,
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": result.title,
          "item": `${BASE_URL}/works/${id}`,
        },
      ],
    };

    return [projectSchema, breadcrumbSchema];
  }, [result, category, id]);

  // 動的OGP画像の取得
  const ogImage = result?.main_image?.storage_path
    ? resolveMediaUrl(result.main_image.storage_path)
    : undefined;

  return (
    <>
      <SEOHead
        title={result ? `${result.title} | 施工実績` : "施工実績詳細"}
        description={
          result
            ? `${result.title}の施工実績詳細。${result.location}にて${formatDate(result.completion_date)}に完工。`
            : "都建設株式会社の施工実績詳細ページです。"
        }
        canonicalUrl={`/works/${id}`}
        keywords="都建設,施工実績,施工事例,解体工事,土木工事,ガーデニング工事,愛媛県,西予市"
        ogType="article"
        ogImage={ogImage}
        publishedTime={result?.completion_date}
        structuredData={structuredData}
        noIndex={notFound}
      />

      <div className="public-page">
        <PublicHeader activePage="works" />

        {/* メインコンテンツ */}
        <main id="main-content" role="main">
          {/* パンくずリスト */}
          <nav className="breadcrumb" aria-label="パンくずリスト">
            <div className="container">
              <Link to="/">ホーム</Link>
              <span className="breadcrumb-separator">/</span>
              <Link to={`${pathPrefix}/works`}>施工実績</Link>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">
                {loading ? "読み込み中..." : result?.title || "詳細"}
              </span>
            </div>
          </nav>

          {/* コンテンツ */}
          <section className="detail-section">
            <div className="container">
              {loading ? (
                <div className="loading-state">
                  <p>読み込み中...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p>{error}</p>
                  <Link to={`${pathPrefix}/works`} className="back-link">
                    施工実績一覧に戻る
                  </Link>
                </div>
              ) : result ? (
                <div className="detail-content">
                  {/* 画像・動画ギャラリー */}
                  <div className="image-gallery">
                    <div className="main-image">
                      {selectedMedia?.type === "video" ? (
                        <video
                          key={selectedMedia.url}
                          src={selectedMedia.url}
                          controls
                          autoPlay
                          preload="metadata"
                          aria-label={`${result.title}の動画`}
                        >
                          お使いのブラウザは動画再生に対応していません。
                        </video>
                      ) : selectedMedia?.type === "image" ? (
                        <img
                          src={selectedMedia.url}
                          alt={`${result.title}の施工写真 - ${category?.name || "施工実績"}${result.location ? ` ${result.location}` : ""}`}
                        />
                      ) : (
                        <div
                          className="placeholder-image"
                          data-text={result.title}
                          role="img"
                          aria-label={`${result.title}の施工写真`}
                        >
                          {result.title}
                        </div>
                      )}
                    </div>
                    {allMedia.length > 1 && (
                      <div className="thumbnail-list">
                        {allMedia.map((media, index) => (
                          <button
                            key={index}
                            className={`thumbnail ${selectedIndex === index ? "active" : ""} ${media.type === "video" ? "thumbnail-video" : ""}`}
                            onClick={() => setSelectedIndex(index)}
                            aria-label={
                              media.type === "video"
                                ? `${result.title}の動画を表示`
                                : `${result.title}の画像 ${index + 1}枚目を表示`
                            }
                          >
                            {media.type === "video" ? (
                              <>
                                <video
                                  src={`${media.url}#t=0.5`}
                                  preload="metadata"
                                  muted
                                  playsInline
                                />
                                <span className="thumbnail-play-icon" aria-hidden="true">
                                  ▶
                                </span>
                              </>
                            ) : (
                              <img
                                src={media.url}
                                alt={`${result.title}のサムネイル画像 ${index + 1}枚目`}
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 詳細情報 */}
                  <div className="detail-info">
                    {category && (
                      <span className="category-badge">{category.name}</span>
                    )}
                    <h1 className="detail-title">{result.title}</h1>

                    {result.description && (
                      <p className="detail-description">{result.description}</p>
                    )}

                    <table className="detail-table">
                      <tbody>
                        {result.client_name && (
                          <tr>
                            <th>発注者</th>
                            <td>{result.client_name}</td>
                          </tr>
                        )}
                        {result.site_area && (
                          <tr>
                            <th>敷地面積</th>
                            <td>{result.site_area}</td>
                          </tr>
                        )}
                        {result.building_area && (
                          <tr>
                            <th>建築面積</th>
                            <td>{result.building_area}</td>
                          </tr>
                        )}
                        {result.total_floor_area && (
                          <tr>
                            <th>延べ面積</th>
                            <td>{result.total_floor_area}</td>
                          </tr>
                        )}
                        {result.structure_type && (
                          <tr>
                            <th>構造</th>
                            <td>{result.structure_type}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div className="detail-actions">
                      <Link to={`${pathPrefix}/works`} className="back-button">
                        施工実績一覧に戻る
                      </Link>
                      <Link to="/contact" className="contact-button-detail">
                        お問い合わせ
                      </Link>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          {/* CTAセクション */}
          <section className="cta-section">
            <div className="container">
              <h2 className="cta-title">お見積もり・ご相談は無料です</h2>
              <p className="cta-text">
                お気軽にお問い合わせください。
                <br />
                お客様のご要望に合わせた最適なプランをご提案いたします。
              </p>
              <div className="cta-buttons">
                <a href="/contact" className="cta-button primary">
                  お問い合わせフォーム
                </a>
                <a href="tel:0894-62-4511" className="cta-button secondary">
                  <span>電話で問い合わせ</span>
                  <span className="phone-number">0894-62-4511</span>
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* フッター */}
        <footer className="public-footer" role="contentinfo">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="/static/miyako-logo-small-white.png"
                srcSet="/static/miyako-logo-small-white.png 1x, /static/miyako-logo-2x-white.png 2x"
                alt="都建設株式会社"
                className="footer-logo-image"
                width={60}
                height={45}
              />
              <span className="footer-company-name">都建設株式会社</span>
            </div>

            <address className="footer-address-block">
              <a
                href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432"
                className="footer-address"
                target="_blank"
                rel="noopener noreferrer"
              >
                〒797-0015 愛媛県西予市宇和町卯之町1丁目432
              </a>

              <a href="tel:0894-62-4511" className="footer-phone">
                お電話：<span itemProp="telephone">0894-62-4511</span>
              </a>
            </address>

            <nav className="footer-nav" aria-label="フッターナビゲーション">
              <a href="/">ホーム</a>
              <a href="/about">会社概要</a>
              <a href="/services">業務内容</a>
              <a href="/works">施工実績</a>
              <a href="/recruit">採用情報</a>
              <a href="/contact">お問い合わせ</a>
            </nav>

            <p className="footer-copyright">
              <small>
                &copy;2024 都建設株式会社 Powered by{" "}
                <a href="#" target="_blank" rel="noopener noreferrer">
                  VERYWEBB
                </a>
              </small>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default WorksDetailPage;
